import {
  useEffect,
  useMemo,
  useRef,
  type FC,
  type RefObject,
  type SyntheticEvent,
} from 'react';

/**
 * HLS oqimini oddiy `<video>` da chizadi.
 *
 * NEGA IFRAME EMAS: Bunny'ning `iframe.mediadelivery.net` pleyeri
 * Telegram Desktop ichidagi brauzerda ochilmaydi — iframe DOM'da
 * turadi, lekin qora bo'lib qoladi va `player.js` orqali javob
 * bermaydi. Telefonda ishlaydi. Iframe ichi boshqa domen bo'lgani
 * uchun sababni tashqaridan ko'rib ham bo'lmaydi.
 *
 * Bu yondashuv bir vaqtning o'zida ikkinchi muammoni ham hal qiladi:
 * ko'rish foizi `postMessage` orqali keladigan `timeupdate` ga
 * tayanardi. Endi brauzerning O'Z hodisasi ishlatiladi — bu yo'l eski
 * `/uploads` videolarida allaqachon sinalgan va ishonchli.
 */
export const HlsVideo: FC<{
  src: string;
  videoRef: RefObject<HTMLVideoElement | null>;
  onTimeUpdate?: (e: SyntheticEvent<HTMLVideoElement>) => void;
  onLoadedMetadata?: (e: SyntheticEvent<HTMLVideoElement>) => void;
  style?: React.CSSProperties;
}> = ({ src, videoRef, onTimeUpdate, onLoadedMetadata, style }) => {
  /*
   * MANZIL BARQAROR BO'LISHI SHART.
   *
   * Server har javobda yangi imzo qo'yadi (`token`, `expires`), ya'ni
   * bir xil videoga ham manzil satri har safar boshqacha bo'ladi. Uni
   * to'g'ridan-to'g'ri bog'liqlikka qo'ysak, kurs ma'lumoti har qayta
   * so'ralganda oqim uzilib, video boshidan ketardi.
   *
   * Shuning uchun videoni YO'L aniqlaydi, so'rov qismi emas.
   */
  const key = useMemo(() => {
    try {
      return new URL(src).pathname;
    } catch {
      return src;
    }
  }, [src]);

  const keyRef = useRef(key);
  const srcRef = useRef(src);
  if (keyRef.current !== key) {
    keyRef.current = key;
    srcRef.current = src;
  }

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const url = srcRef.current;

    /*
     * Safari va iOS HLS ni O'ZI qo'llab-quvvatlaydi — hls.js kerak
     * emas va u yerda ishlatilsa aksincha ziyon qiladi (MSE yo'q).
     */
    if (el.canPlayType('application/vnd.apple.mpegurl')) {
      el.src = url;
      return;
    }

    let cancelled = false;
    let hls: { destroy(): void } | null = null;

    /*
     * DINAMIK IMPORT: hls.js ~400 KB. Uni asosiy to'plamga qo'shsak,
     * videosiz darsni ochgan o'quvchi ham uni mobil internetdan
     * yuklab olardi.
     */
    void import('hls.js').then(({ default: Hls }) => {
      if (cancelled || !videoRef.current) return;
      if (!Hls.isSupported()) {
        // Zaxira: brauzer baribir urinib ko'rsin.
        videoRef.current.src = url;
        return;
      }
      const h = new Hls({ enableWorker: true });
      h.loadSource(url);
      h.attachMedia(videoRef.current);
      hls = h;
    });

    return () => {
      cancelled = true;
      hls?.destroy();
    };
    // Faqat VIDEO o'zgarsa qayta ulanadi — imzo yangilangani uchun emas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <video
      ref={videoRef}
      controls
      playsInline
      controlsList="nodownload"
      style={style}
      onTimeUpdate={onTimeUpdate}
      onLoadedMetadata={onLoadedMetadata}
    />
  );
};
